import React from 'react';
import type { TeamHealthAthlete } from '@/types';
import { REGION_SHORT } from './bodyRegions';

function metricColor(val: number): string {
  if (val <= 3) return 'text-green-500';
  if (val <= 6) return 'text-amber-500';
  return 'text-red-500';
}

function daysSinceLabel(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return '1d ago';
  return `${diff}d ago`;
}

interface Props {
  athlete: TeamHealthAthlete;
  isExpanded: boolean;
  onToggle: () => void;
}

const AthleteHealthRow: React.FC<Props> = ({ athlete, isExpanded, onToggle }) => {
  const checkin = athlete.latest_checkin;
  const painRegions = checkin?.regional_pain?.map(p => REGION_SHORT[p.region] || p.region) ?? [];
  const injuryCount = athlete.active_injury_count;

  // Build injury label: show names if available, fall back to region.
  // active_injuries already excludes resolved records (backend contract).
  const injuryLabels = athlete.active_injuries
    .map(inj => inj.injury_name || (REGION_SHORT[inj.body_region] || inj.body_region));

  return (
    <div
      className={`cursor-pointer border-b border-[var(--card-border)] hover:cgm-bg-secondary transition-colors ${isExpanded ? 'cgm-bg-secondary' : ''}`}
      onClick={onToggle}
      data-tour="team-health-row"
    >
      {/* Desktop: 6-column grid */}
      <div className="hidden md:grid grid-cols-[2fr_80px_80px_120px_1fr_80px] gap-2 px-4 py-3 items-center text-sm">
        {/* Name */}
        <div>
          <span className="font-semibold cgm-text-primary">{athlete.name}</span>
          {checkin && (
            <span className="ml-2 text-[11px] cgm-text-secondary">
              {daysSinceLabel(checkin.report_date)}
            </span>
          )}
        </div>

        {/* Soreness */}
        <div>
          {checkin ? (
            <span className={metricColor(checkin.soreness)}>{checkin.soreness}</span>
          ) : (
            <span className="cgm-text-secondary opacity-50">—</span>
          )}
        </div>

        {/* RPE */}
        <div>
          {checkin ? (
            <span className={metricColor(checkin.rpe)}>{checkin.rpe}</span>
          ) : (
            <span className="cgm-text-secondary opacity-50">—</span>
          )}
        </div>

        {/* Pain Flags */}
        <div className="text-xs truncate">
          {painRegions.length > 0 ? (
            <span className="text-amber-500">{painRegions.join(', ')}</span>
          ) : (
            <span className="cgm-text-secondary opacity-50">None</span>
          )}
        </div>

        {/* Active Injuries */}
        <div>
          {injuryCount > 0 ? (
            <div className="flex flex-wrap gap-1">
              {injuryLabels.slice(0, 2).map((label, i) => (
                <span key={i} className="inline-block text-[11px] px-2 py-0.5 bg-red-500/15 text-red-400 rounded-sm">
                  {label}
                </span>
              ))}
              {injuryLabels.length > 2 && (
                <span className="text-[11px] cgm-text-secondary">+{injuryLabels.length - 2}</span>
              )}
            </div>
          ) : (
            <span className="cgm-text-secondary opacity-50 text-xs">None</span>
          )}
        </div>

        {/* Expand Toggle */}
        <div className="text-right cgm-text-secondary text-xs">
          {isExpanded ? '▲ Collapse' : '▼ Expand'}
        </div>
      </div>

      {/* Mobile: flex card layout */}
      <div className="md:hidden px-4 py-3 space-y-2 text-sm">
        {/* Top: name + expand */}
        <div className="flex items-center justify-between">
          <div>
            <span className="font-semibold cgm-text-primary">{athlete.name}</span>
            {checkin && (
              <span className="ml-2 text-[11px] cgm-text-secondary">
                {daysSinceLabel(checkin.report_date)}
              </span>
            )}
          </div>
          <span className="cgm-text-secondary text-xs">
            {isExpanded ? '▲' : '▼'}
          </span>
        </div>

        {/* Metrics row */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <div>
            <span className="cgm-text-secondary">Soreness: </span>
            {checkin ? (
              <span className={metricColor(checkin.soreness)}>{checkin.soreness}</span>
            ) : (
              <span className="cgm-text-secondary opacity-50">—</span>
            )}
          </div>
          <div>
            <span className="cgm-text-secondary">RPE: </span>
            {checkin ? (
              <span className={metricColor(checkin.rpe)}>{checkin.rpe}</span>
            ) : (
              <span className="cgm-text-secondary opacity-50">—</span>
            )}
          </div>
          {painRegions.length > 0 && (
            <div>
              <span className="cgm-text-secondary">Pain: </span>
              <span className="text-amber-500">{painRegions.join(', ')}</span>
            </div>
          )}
        </div>

        {/* Injuries */}
        {injuryCount > 0 && (
          <div className="flex flex-wrap gap-1">
            {injuryLabels.map((label, i) => (
              <span key={i} className="inline-block text-[11px] px-2 py-0.5 bg-red-500/15 text-red-400 rounded-sm">
                {label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AthleteHealthRow;
